import React, { useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCopy, faCheck } from '@fortawesome/free-solid-svg-icons'
import { truncateUrl } from '../utils'

type Props = {
    url: string
    maxDisplayLength?: number
    stopPropagation?: boolean
    className?: string
}

export default function LinkCopyButton({
    url,
    maxDisplayLength = 35,
    stopPropagation = false,
    className,
}: Props): React.JSX.Element {
    const [hasCopied, setHasCopied] = useState(false)
    const timerRef = useRef<number | null>(null)

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                window.clearTimeout(timerRef.current)
            }
        }
    }, [])

    const handleCopy = async (e: React.MouseEvent) => {
        if (stopPropagation) {
            e.stopPropagation()
        }

        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(url)
            } else {
                const textarea = document.createElement('textarea')
                textarea.value = url
                textarea.setAttribute('readonly', '')
                textarea.style.position = 'fixed'
                textarea.style.left = '-9999px'
                document.body.appendChild(textarea)
                textarea.select()
                document.execCommand('copy')
                document.body.removeChild(textarea)
            }

            setHasCopied(true)
            if (timerRef.current) {
                window.clearTimeout(timerRef.current)
            }
            timerRef.current = window.setTimeout(() => {
                setHasCopied(false)
            }, 2000)
        } catch {
            setHasCopied(false)
        }
    }

    return (
        <span className={`inline-flex items-center gap-1.5 ${className ?? ''}`}>
            <button
                type="button"
                className="text-ctxt-blue hover:underline text-sm"
                title={url}
                onClick={(e) => {
                    if (stopPropagation) {
                        e.stopPropagation()
                        e.preventDefault()
                    }
                    window.open(url, '_blank', 'noopener,noreferrer')
                }}
            >
                {truncateUrl(url, maxDisplayLength)}
            </button>
            <button
                type="button"
                className="inline-flex items-center justify-center"
                onClick={(e) => void handleCopy(e)}
                title="Copy URL"
            >
                <FontAwesomeIcon
                    icon={hasCopied ? faCheck : faCopy}
                    className={`w-3.5 h-3.5 ${
                        hasCopied
                            ? 'text-green-500'
                            : 'text-gray-400 hover:text-gray-600'
                    }`}
                />
            </button>
        </span>
    )
}
